import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { asCurrency, currencyStringToInt } from '../utils/numberUtils';

import { HomeMarker } from './SVGs';
import styles from './styles';

import type Comparables from '../models/Comparables';

interface RentRangeChartProps {
  comparables: Array<Comparables>;
  userPropertyPrice: number;
}

/*
 * Important UI dimensions.
 * Used to determine the absolute positioning of this component.
 */
const HEIGHT = 50;
const MARKER_RADIUS = 4;
const MARKER_BORDER_WIDTH = 1;
const HOME_MARKER_CONTAINER_HEIGHT = 45;
const HOME_MARKER_HEIGHT = 20;
const MARKER_COEFFICIENT = 0.99; // shift markers slightly left to account for width of marker
const HOME_MARKER_OFFSET = 4; // shift home marker more because it is bigger than the comp markers

function getPriceOrZestimate(comparable: Comparables, zestimate: number): number {
  if (comparable.isUsersComp) {
    return zestimate;
  }
  return currencyStringToInt(comparable.bubblePrice || '');
}

const RentRangeChart: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<RentRangeChartProps>>
> = ({ comparables, userPropertyPrice }) => {
  if (comparables.length < 1) {
    return null;
  }
  const min = getPriceOrZestimate(comparables[0], userPropertyPrice);
  const max = getPriceOrZestimate(comparables[comparables.length - 1], userPropertyPrice);
  const range = max - min;

  return (
    <View style={styles.rentRangeChart}>
      <View style={[styles.rentLine, { height: HEIGHT }]}>
        {comparables
          .filter((comp) => !!comp.bubblePrice)
          .map((comp, i) => {
            const price = currencyStringToInt(comp.bubblePrice as string);
            const percentage = ((price - min) / range) * MARKER_COEFFICIENT * 100;
            return (
              <View
                style={[
                  styles.compMarker,
                  {
                    left: `${percentage}%`,
                    height: MARKER_RADIUS * 2,
                    width: MARKER_RADIUS * 2,
                    borderWidth: MARKER_BORDER_WIDTH,
                    top: HEIGHT - MARKER_RADIUS - MARKER_BORDER_WIDTH,
                  },
                ]}
                key={`marker-${i}`}
              />
            );
          })}
        <View
          style={[
            styles.homeMarker,
            {
              left: `${((userPropertyPrice - min) / range) * MARKER_COEFFICIENT * 100 - HOME_MARKER_OFFSET}%`,
              height: HOME_MARKER_CONTAINER_HEIGHT,
              top: HEIGHT - HOME_MARKER_CONTAINER_HEIGHT,
            },
          ]}
        >
          <View style={styles.zestimateLabel}>
            <Text style={styles.h6}>{asCurrency(userPropertyPrice)}</Text>
          </View>
          <View style={[styles.marginAuto, { height: HOME_MARKER_HEIGHT }]}>
            <HomeMarker />
          </View>
        </View>
      </View>
      <View style={styles.flexRow}>
        <View style={styles.minRentLabel}>
          <Text style={styles.h6}>{asCurrency(min)}</Text>
          <Text style={styles.body}>Lowest price</Text>
        </View>
        <View style={styles.extremaSpacer}></View>
        <View style={styles.maxRentLabel}>
          <Text style={styles.h6}>{asCurrency(max)}</Text>
          <Text style={styles.body}>Highest price</Text>
        </View>
      </View>
    </View>
  );
};

export default RentRangeChart;
